import { useCallback, useEffect, useState } from 'react';
import { logger } from '@ui/utils/logger';

const BREAKPOINT = 768;

export const useCollapsibleSidebar = (defaultSize: number = 300, storageKey: string = 'global_sidebar_collapsed') => {
    const [collapsed, setCollapsedState] = useState<boolean>(() => {
        // Auto-collapse on small screens initially
        if (typeof window !== 'undefined' && window.innerWidth < BREAKPOINT) {
            return true;
        }
        try {
            const item = window.localStorage.getItem(storageKey);
            return item ? JSON.parse(item) : false;
        } catch (error) {
            logger.error('Error loading sidebar collapsed state from localStorage', error);
            return false;
        }
    });

    // Initialize size based on persisted collapsed state
    const [sidebarSize, setSidebarSize] = useState<number>(collapsed ? 0 : defaultSize);

    // Helper to persist state
    const persistCollapsedState = useCallback(
        (isCollapsed: boolean) => {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
            } catch (error) {
                logger.error('Error saving sidebar collapsed state to localStorage', error);
            }
        },
        [storageKey]
    );

    // Auto-collapse on resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < BREAKPOINT && !collapsed) {
                setCollapsedState(true);
                setSidebarSize(0);
                persistCollapsedState(true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [collapsed, persistCollapsedState]);

    const toggleSidebar = useCallback(() => {
        if (collapsed) {
            // Expanding: restore default size
            setSidebarSize(defaultSize);
            setCollapsedState(false);
            persistCollapsedState(false);
        } else {
            // Collapsing: set size to 0
            setSidebarSize(0);
            setCollapsedState(true);
            persistCollapsedState(true);
        }
    }, [collapsed, defaultSize, persistCollapsedState]);

    const onSplitterResize = useCallback(
        (sizes: (number | string)[]) => {
            const newSize = typeof sizes[0] === 'number' ? sizes[0] : parseInt(sizes[0], 10) || 0;

            setSidebarSize(newSize);

            const isNowCollapsed = newSize === 0;
            if (isNowCollapsed !== collapsed) {
                setCollapsedState(isNowCollapsed);
                persistCollapsedState(isNowCollapsed);
            }
        },
        [collapsed, persistCollapsedState]
    );

    return {
        collapsed,
        sidebarSize,
        toggleSidebar,
        onSplitterResize,
    };
};
